import { useState, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, List, Grid3X3, Plus, FileText, Eye } from "lucide-react";
import JobDetailDrawer from "./JobDetailDrawer";
import Card from "../../../components/ui/Card";
import ButtonWrapper from "../../components/reuseComp/ButtonWrapper";
import {
  Select,
  Modal,
  Form,
  Input,
  DatePicker,
  InputNumber,
  Button,
  message,
  Popconfirm,
  Spin,
  Empty,
  Tag,
  Tooltip,
  Alert,
  Pagination,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import { IJob, IJobCreatePayload, TJobPriority, TJobStatus } from "../../../types/api/job";
import { useJobList, useJobCreate, useJobUpdate, useJobDelete } from "../../../hooks/api/jobs/hooks";
import { useInvoiceCreate } from "../../../hooks/api/invoices/hooks";
import { useClientList } from "../../../hooks/api/clients/hooks";
import { useFieldWorkerList } from "../../../hooks/api/field-workers/hooks";
import { usePipelineList } from "../../../hooks/api/job-config/hooks";
import { useQuery } from "@tanstack/react-query";
import axios from "../../../lib/axios";
import { API_ENDPOINTS } from "../../../constants/api_endpoints";
import { parseAsInteger, useQueryState } from "nuqs";
import { useTerm } from "../../../lib/terminology";
import { JOB_STATUS_CFG } from "../../../constants/jobStatusConfig";

const STATUS_CFG: Record<TJobStatus, { cls: string; label: string }> = Object.fromEntries(
  Object.entries(JOB_STATUS_CFG).map(([k, v]) => [k, { cls: `${v.bg} ${v.text}`, label: v.label }])
) as Record<TJobStatus, { cls: string; label: string }>;

const PRIORITY_CFG: Record<TJobPriority, { colour: string; label: string }> = {
  low:    { colour: "default", label: "Low" },
  normal: { colour: "blue",    label: "Normal" },
  high:   { colour: "orange",  label: "High" },
  urgent: { colour: "red",     label: "Urgent" },
};

const STATUS_OPTS = [
  { value: "", label: "All Statuses" },
  ...Object.entries(STATUS_CFG).map(([v, c]) => ({ value: v, label: c.label })),
];

// Mirrors the backend's job status state machine (see JobController.save) —
// keeps the edit form from offering transitions the API will reject anyway
// (e.g. completed → scheduled), and makes completed/cancelled read-only.
const VALID_STATUS_TRANSITIONS: Record<string, string[]> = {
  draft: ["draft", "scheduled", "in_progress", "cancelled"],
  scheduled: ["scheduled", "draft", "in_progress", "cancelled"],
  in_progress: ["in_progress", "completed", "cancelled"],
  completed: ["completed"],
  cancelled: ["cancelled"],
};

const PRIORITY_OPTS = Object.entries(PRIORITY_CFG).map(([v, c]) => ({ value: v, label: c.label }));

function getDisplayStatus(job: IJob): { cls: string; label: string } {
  const now = dayjs();
  if (job.scheduled_end && dayjs(job.scheduled_end).isBefore(now)) {
    if (job.status === "scheduled" || job.status === "draft") {
      return { cls: "bg-orange-100 text-orange-700", label: "Expired" };
    }
    if (job.status === "in_progress") {
      return { cls: "bg-red-100 text-red-700", label: "Overdue" };
    }
  }
  return STATUS_CFG[job.status] ?? { cls: "bg-slate-100 text-slate-600", label: job.status };
}



const JobsPage = () => {
  const term = useTerm();
  const [search, setSearch] = useState("");
const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [priorityFilter, setPriorityFilter] = useState<string>("");
  const [dateFilter, setDateFilter] = useState<[Dayjs, Dayjs] | null>(null);
  const [page, setPage] = useState(1);
  const [view, setView] = useState<"list" | "grid">("list");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingJob, setEditingJob] = useState<IJob | null>(null);
  const [detailJob, setDetailJob] = useState<IJob | null>(null);
  const [form] = Form.useForm();
  const [searchParams, setSearchParams] = useSearchParams();
  const [jobId, setJobId] = useQueryState(
  "job_id",
  parseAsInteger
);
  const queryData = useMemo(() => {
  const q: any = {
    page,
    limit: 20,
  };

  if (debouncedSearch.trim()) {
    q.search = debouncedSearch.trim();
  }

  if (statusFilter) {
    q.status = statusFilter;
  }

  if (priorityFilter) {
    q.priority = priorityFilter;
  }

  if (dateFilter) {
    q.scheduled_start_from = dateFilter[0].format('YYYY-MM-DD');
    q.scheduled_start_to = dateFilter[1].format('YYYY-MM-DD');
  }

  return q;
}, [page, debouncedSearch, statusFilter, priorityFilter, dateFilter]);

  const { data: listRes, isLoading, isError, error, refetchWithQuery } = useJobList(queryData);
  const total = Number(listRes?.extra?.total ?? 0);
  const jobs: IJob[] =
  (listRes?.data as IJob[] | undefined) ?? [];

  const { data: clientRes } = useClientList({ page: 1, limit: 200 });
  const clientOptions = ((clientRes?.data as any[]) ?? []).map((c) => {
    const name = c.business_name || `${c.first_name ?? ""} ${c.last_name ?? ""}`.trim() || "—";
    const sub = c.email || c.phone || c.mobile || "";
    return { value: c.id, label: sub ? `${name} (${sub})` : name };
  });

  const { data: workerRes } = useFieldWorkerList({ page: 1, limit: 200, status: "ACTIVE" });
  const workerOptions = ((workerRes?.data as any[]) ?? []).map((w) => {
    const name = `${w.first_name ?? ""} ${w.last_name ?? ""}`.trim() || w.email || "—";
    return { value: w.id, label: w.employee_number ? `${name} (${w.employee_number})` : name };
  });

  const { data: teamRes } = useQuery<any>({
    queryKey: [API_ENDPOINTS.TEAM_LIST],
    queryFn: async () => {
      const res = await axios.get(API_ENDPOINTS.TEAM_LIST);
      return res.data;
    },
    staleTime: 60000,
    refetchOnWindowFocus: false,
  });
  const teamOptions = ((teamRes?.data as any[]) ?? []).map((t: any) => ({
    value: t.id,
    label: t.name,
  }));

  // ── Feature 1: Pipeline stages for status Select ──────────────────────────
  const { data: pipelineRes } = usePipelineList();
  const pipelineStages: any[] = (pipelineRes as any)?.data?.[0]?.stages ?? [];

  const handleJobError = (err: any) => {
    const msg = err?.response?.data?.message ?? err?.message ?? "";
    if (err?.response?.status === 422 && msg.toLowerCase().includes("buffer")) {
      Modal.confirm({
        title: "Scheduling Conflict Warning",
        content: msg,
        okText: "Ok, Got It",
        cancelText: "Go Back",
        onOk: () => {
          message.warning("Please review the schedule conflict before saving.");
        },
      });
    } else {
      message.error(msg || "Failed to save job.");
    }
  };

  const { mutate: createJob, isPending: creating } = useJobCreate({
    qData: queryData,
    onSuccess: () => {
      message.success("Job created.");
      closeModal();
    },
    onError: handleJobError,
  });

  const { mutate: updateJob, isPending: updating } = useJobUpdate({
    qData: queryData,
    onSuccess: () => {
      message.success("Job updated.");
      closeModal();
    },
    onError: handleJobError,
  });

  const { mutate: deleteJob } = useJobDelete({
    qData: queryData,
    onSuccess: () => message.success("Job deleted."),
    onError: (err: any) => message.error(err?.response?.data?.message ?? err?.message ?? "Failed to delete job."),
  });



  useEffect(() => {
  const timer = setTimeout(() => {
    setDebouncedSearch(search);
  }, 500);
  return () => clearTimeout(timer);
}, [search]);

useEffect(() => {
  setPage(1);
}, [debouncedSearch, statusFilter, priorityFilter, dateFilter]);

useEffect(() => {
  refetchWithQuery(queryData);
}, [queryData]);
  function openCreate() {
    setEditingJob(null);
    form.resetFields();
    setModalOpen(true);
  }

  // Deep-link from Dashboard cells: /jobs?status=completed
  useEffect(() => {
    const statusParam = searchParams.get("status");
    if (statusParam) {
      setStatusFilter(statusParam);
      setSearchParams((prev) => {
        prev.delete("status");
        return prev;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Detect ?create=1&start=ISO&end=ISO from Schedule page click-to-create
  useEffect(() => {
    const createParam = searchParams.get("create");
    const startParam = searchParams.get("start");
    const endParam = searchParams.get("end");
    if (createParam === "1") {
      setEditingJob(null);
      form.resetFields();
      if (startParam) {
        const startDayjs = dayjs(startParam);
        const endDayjs = endParam ? dayjs(endParam) : startDayjs.add(1, "hour");
        form.setFieldsValue({
          scheduled_start: startDayjs.isValid() ? startDayjs : undefined,
          scheduled_end: endDayjs.isValid() ? endDayjs : undefined,
        });
      }
      setModalOpen(true);
      // Remove the params so re-navigating back doesn't re-open
      setSearchParams((prev) => {
        prev.delete("create");
        prev.delete("start");
        prev.delete("end");
        return prev;
      });
    }
  }, [searchParams]);

  function openEdit(job: IJob) {
    setEditingJob(job);
    form.resetFields();
    form.setFieldsValue({
      title: job.title,
      description: job.description,
      status: job.status,
      priority: job.priority,
      po_number: job.po_number,
      estimated_value: job.estimated_value,
      notes: job.notes,
      client_id: job.client_id,
      assigned_user_id: job.assigned_user_id,
      team_id: (job as any).assigned_team_id ?? null,
      scheduled_start: job.scheduled_start ? dayjs(job.scheduled_start) : null,
      scheduled_end: job.scheduled_end ? dayjs(job.scheduled_end) : null,
    });
    setModalOpen(true);
  }

  function closeModal() {
    setModalOpen(false);
    setEditingJob(null);
    setJobId(null);
    form.resetFields();
  }

  function handleSubmit(values: any) {
    const payload: IJobCreatePayload = {
      ...values,
      scheduled_start: values.scheduled_start?.toISOString(),
      scheduled_end: values.scheduled_end?.toISOString(),
    };
    if (editingJob) {
      updateJob({ id: editingJob.id, ...payload });
    } else {
      createJob(payload);
    }
  }

  function handleDelete(id: number) {
    deleteJob({ ids: String(id) });
  }

  // ── Quick Invoice ──────────────────────────────────────────────────────────
  const [invoiceJob, setInvoiceJob] = useState<IJob | null>(null);
  const [invoiceForm] = Form.useForm();

  const { mutate: createInvoice, isPending: creatingInvoice } = useInvoiceCreate({
    qData: queryData,
    onSuccess: (res: any) => {
      message.success(`Invoice ${res.data?.invoice_number ?? ""} created.`);
      setInvoiceJob(null);
      invoiceForm.resetFields();
    },
    onError: (err: any) =>
      message.error(err?.response?.data?.message ?? err?.message ?? "Failed to create invoice."),
  });

  function handleQuickInvoice(values: any) {
    if (!invoiceJob) return;
    const unitPrice = invoiceJob.estimated_value ? Number(invoiceJob.estimated_value) : 0;
    createInvoice({
      job_id: invoiceJob.id,
      client_id: (invoiceJob as any).client_id ?? undefined,
      title: values.title,
      invoice_origin: "standalone",
      invoice_type: "standard",
      issue_date: values.issue_date ? dayjs(values.issue_date).format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD"),
      due_date: values.due_date ? dayjs(values.due_date).format("YYYY-MM-DD") : dayjs().add(30, "day").format("YYYY-MM-DD"),
      line_items: [
        {
          description: values.title || invoiceJob.title || "Service",
          quantity: 1,
          unit_price: unitPrice,
          vat_rate: 20,
          line_total: unitPrice,
        },
      ],
    });
  }

useEffect(() => {
  if (!jobId || jobs.length === 0 || modalOpen) return;

  const job = jobs.find((j) => j.id === jobId);

  if (job) {
    openEdit(job);
  }
}, [jobId, jobs, modalOpen]);

  return (
    <div className="min-h-screen">
      {/* HEADER */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-slate-800">{term("job", true)}</h1>
          <p className="text-sm text-slate-500 mt-0.5">Schedule, assign and track all your field service jobs</p>
        </div>
        <ButtonWrapper
          type="primary"
          icon={<Plus size={16} />}
          onClick={openCreate}
          className="flex items-center gap-2"
        >
          New {term("job")}
        </ButtonWrapper>
      </div>

      {/* FILTERS */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Search size={16} className="text-slate-400 absolute left-3 top-2.5 z-10" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={`Search ${term("job", true).toLowerCase()}, ${term("client", true).toLowerCase()}…`}
            className="pl-9 pr-4 h-10 text-sm border border-slate-200 rounded-lg w-full bg-white focus:outline-none focus:border-primary"
          />
        </div>
        <Select
          value={statusFilter || undefined}
          onChange={(v) => setStatusFilter(v ?? "")}
          options={STATUS_OPTS.filter((o) => o.value !== "")}
          className="w-44"
          placeholder="All Statuses"
          allowClear
        />
        <Select
          value={priorityFilter}
          onChange={setPriorityFilter}
          placeholder="All priorities"
          allowClear
          className="w-36"
        >
          <Select.Option value="low">Low</Select.Option>
          <Select.Option value="normal">Normal</Select.Option>
          <Select.Option value="high">High</Select.Option>
          <Select.Option value="urgent">Urgent</Select.Option>
        </Select>
        <DatePicker.RangePicker
          value={dateFilter}
          onChange={(dates) => setDateFilter(dates as [Dayjs, Dayjs] | null)}
          format="DD MMM"
          placeholder={['From date', 'To date']}
          className="w-56"
          allowClear
        />
        <div className="flex gap-1 bg-slate-100 rounded-lg p-1 ml-auto">
          <button
            onClick={() => setView("list")}
            className={`p-1.5 rounded-md ${view === "list" ? "bg-white shadow-sm text-primary" : "text-slate-400"}`}
          >
            <List size={16} />
          </button>
          <button
            onClick={() => setView("grid")}
            className={`p-1.5 rounded-md ${view === "grid" ? "bg-white shadow-sm text-primary" : "text-slate-400"}`}
          >
            <Grid3X3 size={16} />
          </button>
        </div>
      </div>

      {/* ERROR */}
      {isError && (
        <div className="mb-4">
          <Alert
            type="error"
            showIcon
            message="Failed to load data"
            description={(error as any)?.message ?? "Please refresh the page or contact support."}
          />
        </div>
      )}

      {/* CONTENT */}
      {isLoading ? (
        <div className="flex justify-center items-center py-24">
          <Spin size="large" />
        </div>
      ) : jobs.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-100 py-16">
          <Empty
            description={
              <span className="text-slate-500">
                {debouncedSearch || statusFilter || priorityFilter || dateFilter ? "No jobs match your filters." : "No jobs yet."}
              </span>
            }
          >
            {!debouncedSearch && !statusFilter && !priorityFilter && !dateFilter && (
              <Button type="primary" onClick={openCreate}>Create your first job</Button>
            )}
          </Empty>
        </div>
      ) : view === "list" ? (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-xs text-slate-500 font-medium border-b border-slate-100">
                <th className="text-left px-5 py-3">{term("job").toUpperCase()}</th>
                <th className="text-left py-3">{term("client").toUpperCase()} / {term("location").toUpperCase()}</th>
                <th className="text-left py-3">SCHEDULED</th>
                <th className="text-left py-3">{term("worker").toUpperCase()}</th>
                <th className="text-left py-3">PRIORITY</th>
                <th className="text-left py-3">STATUS</th>
                <th className="text-left py-3">VALUE</th>
                <th className="py-3"></th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((j) => {
                const { cls, label } = getDisplayStatus(j);
                const priority = PRIORITY_CFG[j.priority];
                return (
                  <tr
                    key={j.id}
                    className="border-t border-slate-50 hover:bg-slate-50 cursor-pointer transition-colors"
                  >
                    <td className="px-5 py-3">
                      <div className="font-medium text-slate-800 text-sm">{j.job_number}</div>
                      <div className="text-xs text-slate-400 truncate max-w-[180px]">{j.title}</div>
                    </td>
                    <td className="py-3 text-slate-600 text-sm">
                      <div>{j.client_name ?? "—"}</div>
                      {j.site_name && (
                        <div className="text-xs text-slate-400 truncate max-w-[140px]">{j.site_name}</div>
                      )}
                    </td>
                    <td className="py-3 text-slate-500 text-sm whitespace-nowrap">
                      {j.scheduled_start
                        ? dayjs(j.scheduled_start).format("DD/MM/YYYY HH:mm")
                        : "—"}
                    </td>
                    <td className="py-3 text-slate-600 text-sm">
                      {j.assigned_user_name ?? "Unassigned"}
                    </td>
                    <td className="py-3">
                      <Tag color={priority.colour}>{priority.label}</Tag>
                    </td>
                    <td className="py-3">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${cls}`}>
                        {label}
                      </span>
                    </td>
                    <td className="py-3 font-medium text-slate-700 text-sm">
                      {j.estimated_value != null ? `£${Number(j.estimated_value).toFixed(2)}` : "—"}
                    </td>
                    <td className="py-3 pr-5">
                      <div className="flex items-center gap-2">
                        <Tooltip title="View details">
                          <button
                            onClick={() => setDetailJob(j)}
                            className="text-xs text-slate-500 hover:text-slate-700 font-medium flex items-center gap-1"
                          >
                            <Eye size={12} /> View
                          </button>
                        </Tooltip>
                        <button
                          onClick={() => openEdit(j)}
                          className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                        >
                          Edit
                        </button>
                        <Tooltip title={`Create ${term("invoice")}`}>
                          <button
                            onClick={() => {
                              setInvoiceJob(j);
                              invoiceForm.setFieldsValue({
                                title: j.title,
                                issue_date: dayjs(),
                                due_date: dayjs().add(30, "day"),
                              });
                            }}
                            className="text-xs text-emerald-600 hover:text-emerald-800 font-medium"
                          >
                            Invoice
                          </button>
                        </Tooltip>
                        <Popconfirm
                          title="Delete this job?"
                          description="This action cannot be undone."
                          onConfirm={() => handleDelete(j.id)}
                          okText="Yes"
                          cancelText="No"
                          okButtonProps={{ danger: true }}
                        >
                          <button className="text-xs text-red-500 hover:text-red-700 font-medium">
                            Delete
                          </button>
                        </Popconfirm>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {jobs.map((j) => {
            const { cls, label } = getDisplayStatus(j);
            const priority = PRIORITY_CFG[j.priority];
            return (
              <Card
                key={j.id}
                className="p-5 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="font-semibold text-slate-800 text-sm">{j.job_number}</div>
                    <div className="text-xs text-slate-500 mt-0.5 line-clamp-1">{j.title}</div>
                  </div>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${cls}`}>
                    {label}
                  </span>
                </div>
                <div className="text-xs text-slate-600 mb-2">{j.client_name ?? "—"}</div>
                <div className="flex items-center justify-between border-t border-slate-50 pt-3">
                  <div className="flex items-center gap-2">
                    <Tag color={priority.colour} className="text-xs">{priority.label}</Tag>
                    <span className="text-xs text-slate-500">{j.assigned_user_name ?? "Unassigned"}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Tooltip title="View details">
                      <button
                        onClick={() => setDetailJob(j)}
                        className="text-xs text-slate-500 hover:text-slate-700 font-medium flex items-center gap-1"
                      >
                        <Eye size={12} /> View
                      </button>
                    </Tooltip>
                    <Tooltip title="Create invoice">
                      <button
                        onClick={() => {
                          setInvoiceJob(j);
                          invoiceForm.setFieldsValue({
                            title: j.title,
                            issue_date: dayjs(),
                            due_date: dayjs().add(30, 'day'),
                          });
                        }}
                        className="text-xs text-emerald-600 hover:text-emerald-800 font-medium flex items-center gap-1"
                      >
                        <FileText size={12} /> Invoice
                      </button>
                    </Tooltip>
                    <button onClick={() => openEdit(j)} className="text-xs text-primary font-medium">Edit</button>
                    <Popconfirm
                      title="Delete this job?"
                      description="This action cannot be undone."
                      onConfirm={() => handleDelete(j.id)}
                      okButtonProps={{ danger: true }}
                    >
                      <button className="text-xs text-red-500 font-medium">Delete</button>
                    </Popconfirm>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* PAGINATION */}
      {!isLoading && total > 0 && (
        <div className="flex justify-end mt-4">
          <Pagination
            current={page}
            pageSize={20}
            total={total}
            showTotal={(t) => `${t} jobs`}
            onChange={(p) => setPage(p)}
            showSizeChanger={false}
          />
        </div>
      )}

      {/* QUICK INVOICE MODAL */}
      <Modal
        open={!!invoiceJob}
        onCancel={() => { setInvoiceJob(null); invoiceForm.resetFields(); }}
        title={
          <span className="text-base font-semibold">
            Create {term("invoice")} — {invoiceJob?.job_number}
          </span>
        }
        width="min(460px, 95vw)"
        footer={[
          <Button key="cancel" onClick={() => { setInvoiceJob(null); invoiceForm.resetFields(); }}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={creatingInvoice}
            onClick={() => invoiceForm.submit()}
          >
            Create {term("invoice")}
          </Button>,
        ]}
        destroyOnClose
      >
        <Form form={invoiceForm} layout="vertical" size="middle" className="pt-2" onFinish={handleQuickInvoice}>
          <Form.Item name="title" label={`${term("invoice")} Title`} rules={[{ required: true }]}>
            <Input placeholder="e.g. Service invoice" />
          </Form.Item>
          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item name="issue_date" label="Issue Date">
              <DatePicker className="w-full" format="DD/MM/YYYY" />
            </Form.Item>
            <Form.Item name="due_date" label="Due Date">
              <DatePicker className="w-full" format="DD/MM/YYYY" />
            </Form.Item>
          </div>
          <p className="text-xs text-slate-400">
            A draft {term("invoice").toLowerCase()} will be created linked to this {term("job").toLowerCase()}. Add line items from the {term("invoice", true).toLowerCase()} page.
          </p>
        </Form>
      </Modal>

      {/* JOB DETAIL DRAWER */}
      <JobDetailDrawer
        job={detailJob}
        onClose={() => setDetailJob(null)}
        onEdit={(j) => { setDetailJob(null); openEdit(j); }}
      />

      {/* CREATE / EDIT MODAL */}
      <Modal
        open={modalOpen}
        onCancel={closeModal}
        title={
          <span className="text-base font-semibold">
            {editingJob ? `Edit ${editingJob.job_number}` : `Create New ${term("job")}`}
          </span>
        }
        width="min(580px, 95vw)"
        footer={[
          <Button key="cancel" onClick={closeModal}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={creating || updating}
            onClick={() => form.submit()}
          >
            {editingJob ? "Save Changes" : `Create ${term("job")}`}
          </Button>,
        ]}
        destroyOnClose
      >
        <Form form={form} layout="vertical" size="middle" className="pt-2" onFinish={handleSubmit}>
          <Form.Item
            name="title"
            label={`${term("job")} Title`}
            rules={[{ required: true, message: "Title is required" }]}
          >
            <Input placeholder={`e.g. Annual ${term("job").toLowerCase()} description`} />
          </Form.Item>

          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item
              name="client_id"
              label={term("client")}
            >
              <Select
                showSearch
                allowClear
                placeholder={`Select ${term("client").toLowerCase()}…`}
                options={clientOptions}
                filterOption={(input, opt) =>
                  ((opt?.label as string) ?? "").toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>
            <Form.Item name="assigned_user_id" label={`Assigned ${term("worker")}`}>
              <Select
                showSearch
                allowClear
                placeholder={`Assign ${term("worker").toLowerCase()}…`}
                options={workerOptions}
                filterOption={(input, opt) =>
                  ((opt?.label as string) ?? "").toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>
          </div>

          <Form.Item name="team_id" label="Team">
            <Select
              showSearch
              allowClear
              placeholder="Assign to a team (optional)…"
              options={teamOptions}
              filterOption={(input, opt) =>
                ((opt?.label as string) ?? "").toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>

          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item
              name="status"
              label="Status"
              initialValue="draft"
              extra={
                editingJob && (editingJob.status === "completed" || editingJob.status === "cancelled")
                  ? `${editingJob.status === "completed" ? "Completed" : "Cancelled"} jobs cannot be reopened.`
                  : undefined
              }
            >
              <Select disabled={!!editingJob && ["completed", "cancelled"].includes(editingJob.status)}>
                <Select.OptGroup label="Default">
                  {STATUS_OPTS.filter((o) => o.value !== "")
                    .filter((o) => !editingJob || (VALID_STATUS_TRANSITIONS[editingJob.status] ?? Object.keys(STATUS_CFG)).includes(o.value))
                    .map((o) => (
                      <Select.Option key={o.value} value={o.value}>{o.label}</Select.Option>
                    ))}
                </Select.OptGroup>
                {pipelineStages.length > 0 && (!editingJob || editingJob.status !== "completed") && (
                  <Select.OptGroup label="Pipeline Stages">
                    {pipelineStages.map((s: any) => (
                      <Select.Option key={s.id} value={s.slug}>{s.name}</Select.Option>
                    ))}
                  </Select.OptGroup>
                )}
              </Select>
            </Form.Item>
            <Form.Item name="priority" label="Priority" initialValue="normal">
              <Select options={PRIORITY_OPTS} />
            </Form.Item>
          </div>

          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item name="scheduled_start" label="Scheduled Start">
              <DatePicker showTime className="w-full" format="DD/MM/YYYY HH:mm" />
            </Form.Item>
            <Form.Item name="scheduled_end" label="Scheduled End">
              <DatePicker showTime className="w-full" format="DD/MM/YYYY HH:mm" />
            </Form.Item>
          </div>

          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item name="estimated_value" label="Estimated Value (£)">
              <InputNumber className="w-full" placeholder="0.00" min={0} prefix="£" />
            </Form.Item>
            <Form.Item name="po_number" label="PO Number">
              <Input placeholder="Client PO reference…" />
            </Form.Item>
          </div>

          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} placeholder="Job description…" />
          </Form.Item>

          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} placeholder="Internal notes…" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default JobsPage;
